<template>
  <div v-if="!!phaseData">
    <div class="d-flex align-center justify-space-between" :class="{ 'mt-4': $vuetify.breakpoint.smAndDown }">
      <div>
        <span class="mb-0 mr-2 text-h5" v-if="$vuetify.breakpoint.mdAndUp">Selecione as opções abaixo:</span>
      </div>
      <div>
        <v-menu offset-y>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              title="Enviar para o paciente preencher"
              text
              class="text-none"
              color="primary"
              v-bind="attrs"
              v-on="on"
            >
              <v-icon class="mr-2">mdi-cellphone-check</v-icon>
              Enviar para paciente
            </v-btn>
          </template>
          <v-list rounded>
            <v-list-item link @click="copyToClipboard">
              <v-icon class="mr-2">mdi-link</v-icon>
              <v-list-item-title class="item-title"> Copiar link </v-list-item-title>
            </v-list-item>
            <v-list-item link @click="openWhatsAppLink">
              <v-icon class="mr-2">mdi-whatsapp</v-icon>
              <v-list-item-title class="item-title">
                Compartilhar por WhatsApp <br />
                {{ patientMobile }}
              </v-list-item-title>
            </v-list-item>
            <v-list-item link @click="sendSms">
              <v-icon class="mr-2">mdi-message-processing-outline</v-icon>
              <v-list-item-title class="item-title">
                Enviar por SMS<br />{{ patientMobile }}
                <v-list-item-subtitle v-if="smsCooldown"
                  >Aguarde {{ resendSmsSeconds }}s para poder enviar novamente
                </v-list-item-subtitle>
              </v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </div>
    </div>
    <SideResult
      title=""
      :show-buttons="false"
      :metrics="paramData"
      :compact="$vuetify.breakpoint.smAndDown"
      v-if="!phaseData.resultado_manual"
      class="w-480 py-8"
    />

    <div class="d-flex">
      <text-field
        class="mr-4"
        :min="0"
        type="number"
        v-if="phaseData.resultado_manual"
        v-model="phaseData.dlqi.value"
        :max="30"
      />
      <v-btn-toggle
        class="mt-1"
        borderless
        color="primary"
        v-model="phaseData.resultado_manual"
        @change="$emit('btn:toggle', phaseData.resultado_manual)"
      >
        <v-btn
          :value="true"
          text
          color="primary"
          :class="[
            'text-none',
            { 'bg-active': phaseData.resultado_manual, 'bg-inactive': !phaseData.resultado_manual },
          ]"
        >
          Aplicar resultado manualmente
        </v-btn>
      </v-btn-toggle>
    </div>

    <DlqiCalculator
      v-model="paramData"
      ref="calculator"
      :fill-data="answers"
      @input="updatePhaseData"
      @calculate="onCalculate"
      v-if="!phaseData.resultado_manual && answers"
      class="w-full py-8 border-r with-scroll"
    />

    <attendance-step-note class="mt-8 py-2" />
  </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'
import BasePhase from '@/components/AttendanceForm/general/phase/BasePhase.vue'
import TextField from '@/components/TextField.vue'
import { DLQI_OPTIONS } from '@/components/AttendanceForm/general/constants/dlqiOptions'
import SideResult from '@/components/CalculatorContent/partial/SideResult.vue'
import DlqiCalculator from '@/components/CalculatorContent/DLQI.vue'
import AttendanceStepNote from '@/components/AttendanceForm/psoriasis/phase/v2/AttendanceStepNote.vue'

const { mapGetters: attendanceGettersMap } = createNamespacedHelpers('attendance')
const { mapGetters: authGettersMap } = createNamespacedHelpers('auth')

export default {
  extends: BasePhase,
  components: { AttendanceStepNote, DlqiCalculator, SideResult, TextField },
  computed: {
    ...attendanceGettersMap(['getPhaseDataByKey', 'attendancePatient', 'attendance', 'patientMobile']),
    ...authGettersMap(['pharmaceuticalDomainConfig']),
    defaultDlqiOptions() {
      return [
        { text: 'Realmente muito', value: 3 },
        { text: 'Bastante', value: 2 },
        { text: 'Um pouco', value: 1 },
        { text: 'Nada', value: 0 },
      ]
    },
    computedDlqi() {
      if (!this.phaseData?.profile || this.phaseData?.resultado_manual) {
        return this.phaseData?.dlqi?.value || 0
      }

      let result = 0
      for (let option of this.dlqiOptions) {
        let value = this.phaseData.profile[option.key]?.value || 0

        if (!parseInt(value)) {
          continue
        }

        result += value
      }
      return result
    },
    dlqiOptions() {
      const patientAge = this.$util.toAgeNumber(this.attendancePatient.birthday)
      if (patientAge < 4) {
        return DLQI_OPTIONS.CHILD
      }
      if (patientAge < 18) {
        return DLQI_OPTIONS.MINOR
      }
      return DLQI_OPTIONS.ADULT
    },
  },
  data() {
    return {
      paramData: [],
      answers: undefined,
      smsCooldown: false,
      resendSmsSeconds: 60,
    }
  },
  async mounted() {
    await this.fillAnswers()
  },
  methods: {
    async fillAnswers() {
      // fill profile from calculators
      if (!Array.isArray(this.phaseData.profile)) {
        const profile = this.phaseData.profile

        this.phaseData.profile = []
        for (const key of Object.keys(profile)) {
          if (key === 'done' && key === 'startedAt') {
            continue
          }
          this.phaseData.profile.push({ [key]: { value: profile[key] } })
        }
      }

      // fill profile from survey
      const surveyId = this.phaseData.sentSurveys && this.phaseData.sentSurveys['DLQI']
      if (surveyId) {
        const survey = await this.$api.getSurveyByUuid(surveyId)
        this.phaseData.profile = []
        const keys = survey.data ? Object.keys(survey.data) : []
        for (const key of keys) {
          if (key === 'done' || key === 'startedAt') {
            continue
          }
          this.phaseData.profile.push({ [key]: { value: survey.data[key].value } })
        }
      }

      // fill default
      this.answers = this.phaseData.profile.reduce((acc, p) => {
        const key = Object.keys(p)[0]
        acc[key] = Object.values(p)[0].value
        return acc
      }, {})
    },
    async openWhatsAppLink() {
      if (!this.patientMobile) {
        return null
      }

      let surveyId = this.phaseData.sentSurveys && this.phaseData.sentSurveys['DLQI']

      if (!surveyId) {
        surveyId = await this.createSurveyLink()
      }

      const mobileNumber = this.$util.toNumbersOnly(this.patientMobile)
      const url = `${window.location.origin}/survey/${surveyId}`
      const message = this.$util.urlEncode(`Responda o formulário em ${url}`)
      const href = `https://api.whatsapp.com/send?phone=55${mobileNumber}&text=${message}&lang=pt_br`
      window.open(href, '_blank')
    },
    async copyToClipboard() {
      let surveyId = this.phaseData.sentSurveys && this.phaseData.sentSurveys['DLQI']

      if (!surveyId) {
        surveyId = await this.createSurveyLink()
      }

      const url = `${window.location.origin}/survey/${surveyId}`
      this.$util.copyToClipboard(url)
      this.$root.ok('Link de preenchimento copiado')
    },
    updatePhaseData(data) {
      const dlqi = data.find((d) => d.name === 'DLQI')
      if (dlqi) {
        this.phaseData.dlqi.value = dlqi.value
      }
    },
    onCalculate(data) {
      this.phaseData.profile = Object.keys(data).map((key) => ({ [key]: { value: data[key] } }))
    },
    getDefaultData() {
      return {
        dlqi: { value: null },
        resultado_manual: false,
        profile: this.buildEmptyProfile(),
      }
    },
    async createSurveyLink() {
      try {
        this.sending = true
        let newSurvey = await this.$api.createSurveyFromAttendance(
          this.attendance.id,
          this.attendance.patient.id,
          this.attendance.physician.id,
          'DLQI',
        )
        this.phaseData.sentSurveys = { ...this.phaseData.sentSurveys, DLQI: newSurvey.uuid }
        return newSurvey.uuid
      } catch (error) {
        console.log(error)
      } finally {
        this.sending = false
      }
    },
    async sendSurveyLink(surveyId) {
      const surveysToSendLink = []
      surveysToSendLink.push({
        type: 'DLQI',
        uuid: surveyId,
      })
      try {
        await this.$api.sendSurveyToPatient(surveysToSendLink, this.attendance.patient.id, this.attendance.physician.id)
        this.$root.ok('Link de preenchimento enviado para paciente')
      } catch (error) {
        console.log(error)
        this.$root.error('Não foi possível enviar os links, tente novamente mais tarde.')
      } finally {
      }
    },
    async sendSms() {
      try {
        this.smsCooldown = true
        let surveyId = this.phaseData.sentSurveys && this.phaseData.sentSurveys['DLQI']
        if (!surveyId) {
          surveyId = await this.createSurveyLink()
        }
        await this.sendSurveyLink(surveyId)
        setTimeout(() => (this.smsCooldown = false), this.resendSmsSeconds * 1000)
      } catch (error) {
        this.smsCooldown = false
        this.$root.error(error)
      }
    },
    buildEmptyProfile: function () {
      const profile = []
      for (const option of this.dlqiOptions) {
        profile.push({ [option.key]: { value: null } })
      }
      return profile
    },
  },
  watch: {
    computedDlqi(newVal) {
      if (this.phaseData.resultado_manual) {
        return
      }
      this.phaseData.dlqi.value = Number(newVal)
    },
  },
}
</script>

<style scoped>
.big-number {
  font-weight: medium;
  color: green;
  font-size: 2.5rem;
}

.area-box {
  border: 1px solid grey;
  border-radius: 10px;
}

.slider-image {
  border: 4px solid #1976d2;
}

.bg-active {
  background-color: #e0f0ff !important;
}

.bg-inactive {
  background-color: #ffffff !important;
  opacity: 1 !important;
}

.v-btn.v-item--active::before {
  background-color: #ffffff;
}

.item-title {
  line-height: 24px;
}
</style>
